import { Annotation, TodoOption } from 'interfaces/annotation';
import { findIndex, forEach, map, reduce, some, toLower, trim } from 'lodash';

export interface DuplicateTodoOptionGroup {
  nameKey: string;
  label: string;
  canonicalOption: TodoOption;
  duplicateOptions: TodoOption[];
}

export interface ClassifiedTodoOptions {
  activeOptions: TodoOption[];
  archivedOptions: TodoOption[];
  duplicateGroups: DuplicateTodoOptionGroup[];
}

export interface UnconfiguredAnnotationValue {
  nameKey: string;
  label: string;
  option: TodoOption;
  savedCount: number;
  draftCount: number;
}

interface TodoOptionGroup {
  nameKey: string;
  options: TodoOption[];
}

export const getTodoOptionNameKey = (option: TodoOption) => toLower(trim(option.name));

const groupTodoOptionsByName = (todoOptions: TodoOption[]): TodoOptionGroup[] =>
  reduce(
    todoOptions,
    (groups: TodoOptionGroup[], option, optionIndex) => {
      const normalizedName = getTodoOptionNameKey(option);
      const nameKey = normalizedName || `__unnamed-${optionIndex}`;
      const existingGroupIndex = findIndex(groups, { nameKey });

      if (existingGroupIndex === -1) {
        return [...groups, { nameKey, options: [option] }];
      }

      return map(groups, (group, groupIndex) =>
        groupIndex === existingGroupIndex ? { ...group, options: [...group.options, option] } : group
      );
    },
    []
  );

// Every persisted option must remain visible (QMS requirement): this only labels
// duplicates for cleanup, it never removes a record from activeOptions/archivedOptions.
export const classifyTodoOptions = (todoOptions: TodoOption[]): ClassifiedTodoOptions => {
  const groups = groupTodoOptionsByName(todoOptions);
  const activeOptions: TodoOption[] = [];
  const archivedOptions: TodoOption[] = [];
  const duplicateGroups: DuplicateTodoOptionGroup[] = [];

  forEach(groups, (group) => {
    forEach(group.options, (option) => {
      if (option.deletedAt) {
        archivedOptions.push(option);
      } else {
        activeOptions.push(option);
      }
    });

    if (group.options.length <= 1 || group.nameKey.startsWith('__unnamed-')) {
      return;
    }

    const activeInGroup = group.options.filter((option) => !option.deletedAt);
    const canonicalOption = activeInGroup[0] ?? group.options[0];
    duplicateGroups.push({
      nameKey: group.nameKey,
      label: trim(canonicalOption.displayName || canonicalOption.name),
      canonicalOption,
      duplicateOptions: group.options.filter((option) => option !== canonicalOption),
    });
  });

  return { activeOptions, archivedOptions, duplicateGroups };
};

export const getDuplicateNameKeySet = (todoOptions: TodoOption[]): Set<string> => {
  const counts = new Map<string, number>();
  forEach(todoOptions, (option) => {
    const nameKey = getTodoOptionNameKey(option);
    if (!nameKey) {
      return;
    }
    counts.set(nameKey, (counts.get(nameKey) ?? 0) + 1);
  });

  const duplicateNameKeys = new Set<string>();
  counts.forEach((count, nameKey) => {
    if (count > 1) {
      duplicateNameKeys.add(nameKey);
    }
  });
  return duplicateNameKeys;
};

// Persisted duplicates can share an identical class key, so rows must be targeted by
// position within their own segment, never by the (possibly colliding) class key.
export type TodoOptionRowSegment = 'active' | 'archived' | 'removalPending';

export const buildTodoOptionRowId = (segment: TodoOptionRowSegment, index: number) => `${segment}::${index}`;

export const parseTodoOptionRowId = (rowId: string): { segment: TodoOptionRowSegment; index: number } => {
  const [segment, indexText] = rowId.split('::');
  return { segment: segment as TodoOptionRowSegment, index: Number(indexText) };
};

export const deduplicateTodoOptionsByName = (
  todoOptions: TodoOption[]
): { options: TodoOption[]; skippedDuplicateNames: string[] } =>
  reduce(
    todoOptions,
    (result, option) => {
      const nameKey = getTodoOptionNameKey(option);
      const isDuplicate =
        Boolean(nameKey) && some(result.options, (existing) => getTodoOptionNameKey(existing) === nameKey);

      if (!isDuplicate) {
        return { ...result, options: [...result.options, option] };
      }

      const duplicateLabel = trim(option.displayName || option.name);
      return {
        ...result,
        skippedDuplicateNames: some(result.skippedDuplicateNames, (name) => toLower(name) === toLower(duplicateLabel))
          ? result.skippedDuplicateNames
          : [...result.skippedDuplicateNames, duplicateLabel],
      };
    },
    { options: [] as TodoOption[], skippedDuplicateNames: [] as string[] }
  );

const getSummaryCount = (value: unknown): number => {
  if (typeof value === 'number') {
    return value;
  }
  if (!value || typeof value !== 'object') {
    return 0;
  }

  return reduce(
    value as Record<string, unknown>,
    (count, nestedValue) => count + (typeof nestedValue === 'number' ? nestedValue : 0),
    0
  );
};

export const getUnconfiguredAnnotationValues = ({
  annotations,
  configuredOptions,
  resolveOption,
}: {
  annotations: Annotation[];
  configuredOptions: TodoOption[];
  resolveOption: (classId: string) => TodoOption;
}): UnconfiguredAnnotationValue[] => {
  const configuredNameKeys = map(configuredOptions, getTodoOptionNameKey);
  const valuesByName = new Map<string, UnconfiguredAnnotationValue>();

  const collectSummary = (summary: Annotation['annotationSummary'], countKey: 'savedCount' | 'draftCount') => {
    forEach(summary, (summaryValue, classId) => {
      const option = resolveOption(classId);
      const nameKey = getTodoOptionNameKey(option);
      if (!nameKey || configuredNameKeys.includes(nameKey)) {
        return;
      }

      const existingValue = valuesByName.get(nameKey);
      const count = getSummaryCount(summaryValue);
      if (existingValue) {
        valuesByName.set(nameKey, { ...existingValue, [countKey]: existingValue[countKey] + count });
        return;
      }

      valuesByName.set(nameKey, {
        nameKey,
        label: trim(option.displayName || option.name),
        option,
        savedCount: countKey === 'savedCount' ? count : 0,
        draftCount: countKey === 'draftCount' ? count : 0,
      });
    });
  };

  forEach(annotations, (annotation) => {
    collectSummary(annotation.annotationSummary, 'savedCount');
    collectSummary(annotation.draftAnnotationSummary, 'draftCount');
  });

  return Array.from(valuesByName.values());
};
